import { createFeatureSelector, createSelector, DefaultProjectorFn } from '@ngrx/store';
import { ISitesState, SITES_STATE_KEY, sitesAdapter } from '../reducers/sites.reducer';
import { IAccount, ISite } from '../../../shared/models';
import { ACCOUNTS_SELECTORS } from './accounts.selectors';
import { MemoizedSelector } from '@ngrx/store/src/selector';
import { Dictionary } from '@ngrx/entity';

const { selectEntities, selectAll } = sitesAdapter.getSelectors();

export const selectSitesState = createFeatureSelector<ISitesState>(SITES_STATE_KEY);

const selectSiteEntities = createSelector(selectSitesState, selectEntities);
const selectAllSites = createSelector(selectSitesState, selectAll);
const isLoading = createSelector(selectSitesState, ({ loading }) => loading);
const isError = createSelector(selectSitesState, ({ error }) => Boolean(error));

const selectSitesFilteredByAccounts = (
  selectedAccounts: IAccount[] | null,
): MemoizedSelector<object, ISite[], DefaultProjectorFn<ISite[]>> =>
  createSelector(selectAllSites, ACCOUNTS_SELECTORS.selectAccounts, (sites: ISite[], allAccounts: IAccount[]) => {
    if (!selectedAccounts?.length) {
      return [];
    }

    if (allAccounts.length === selectedAccounts.length) {
      return sites;
    }

    return sites.filter((site) => selectedAccounts.find((account) => account.accountId === site.accountId));
  });

const selectSitesFilteredByActiveState = (
  sites: ISite[],
  isShowInactive: boolean | null,
): MemoizedSelector<object, ISite[], DefaultProjectorFn<ISite[]>> =>
  createSelector(() => (isShowInactive ? sites : sites.filter(({ siteIsActive }) => siteIsActive)));

const selectActiveSitesWithStatusFilteredByAccountId = (
  selectedAccountId: number | null,
): MemoizedSelector<object, ISitesWithLoadingStatus> =>
  createSelector(selectAllSites, isLoading, (sites, isLoading) => ({
    sites: sites.filter(({ siteIsActive, accountId }) => siteIsActive && accountId === selectedAccountId),
    isLoading,
  }));

const selectSitesByAccountId = (id?: number | null): MemoizedSelector<object, ISite[], DefaultProjectorFn<ISite[]>> =>
  createSelector(selectAllSites, (sites) =>
    !id || !Array.isArray(sites) ? [] : sites.filter(({ accountId }) => accountId === id),
  );

const selectSitesByIds = (ids: number[]): MemoizedSelector<object, ISite[], (s1: Dictionary<ISite>) => ISite[]> =>
  createSelector(selectSiteEntities, (dictionary): ISite[] => ids?.map((id) => dictionary[id] as ISite));

export const SITES_SELECTORS = {
  selectSiteEntities,
  selectAllSites,
  selectSitesFilteredByAccounts,
  selectSitesFilteredByActiveState,
  selectActiveSitesWithStatusFilteredByAccountId,
  selectSitesByAccountId,
  selectSitesByIds,
  isLoading,
  isError,
};

export interface ISitesWithLoadingStatus {
  sites: ISite[];
  isLoading: boolean;
}
